import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { useCallback, useMemo, useRef } from 'react';

import { GET_USER } from '@repo/api-core-toolkit';
import { ActiveUser } from '@repo/types-client';

import { useDatabaseQuery } from '../hooks';
import { getAddresses } from './_utils/get-addresses';

interface UseUserOptions {
  username?: string;
}

export const useUser = <T = ActiveUser>({ username }: UseUserOptions = {}): {
  user: T | null;
  isDynamicUser: boolean;
  addresses: string[];
  isLoading: boolean;
  isAuthUser: boolean;
} => {
  const { user: dynamicUser } = useDynamicContext();

  const addressesRef = useRef<string[]>([]);

  const isDynamicUser = !username;

  const { data: databaseUser, isLoading } = useDatabaseQuery<ActiveUser | null>(
    {
      queryKey: ['user', username],
      query: GET_USER,
      variables: {
        where: {
          username: username,
        },
      },
      field: 'getUser',
      enabled: !!username,
    },
  );

  const finalUser: ActiveUser | null = useMemo(() => {
    if (isDynamicUser) return dynamicUser ?? null;
    return databaseUser ?? null;
  }, [isDynamicUser, dynamicUser, databaseUser]);

  const isAuthUser = useMemo(() => {
    if (!dynamicUser) return false;
    return isDynamicUser || databaseUser?.username === dynamicUser.username;
  }, [dynamicUser, databaseUser, isDynamicUser]);

  const resolveAddresses = useCallback(() => {
    if (!finalUser) return [];
    if (isAuthUser) {
      return dynamicUser ? getAddresses(dynamicUser, true) : [];
    }
    return getAddresses(finalUser, isDynamicUser);
  }, [finalUser, isAuthUser, isDynamicUser, dynamicUser]);

  const addresses = useMemo(() => {
    const resolvedAddresses = resolveAddresses();
    if (
      resolvedAddresses.length === addressesRef.current.length &&
      resolvedAddresses.every((x) => addressesRef.current.includes(x))
    ) {
      return addressesRef.current;
    }
    addressesRef.current = resolvedAddresses;
    return addressesRef.current;
  }, [resolveAddresses]);

  return {
    user: finalUser as T,
    isDynamicUser,
    addresses,
    isLoading,
    isAuthUser,
  };
};
